import { DeleteTravelEsimDialog } from '@/components/DeleteTravelEsimDialog';
import { Button } from '@/components/ui/button';
import { use404Redirect } from '@/hooks/use404Redirect';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { useTravelEsim } from '@/hooks/useTravelEsimOrders';
import { fadeUp, stagger } from '@/lib/animations';
import { getUserRole } from '@/lib/auth/token';
import { localDateKey } from '@/lib/format';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { TravelEsimHeroCard } from './travel-esim-details/TravelEsimHeroCard';
import { TravelEsimPlansCard } from './travel-esim-details/TravelEsimPlansCard';

/** Detail page of an international eSIM: the eSIM first, then its plans. */
export function TravelEsimDetailView() {
  const { itemId: id } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { tenantSlug } = useTenantSlug();
  const { data: esim, isLoading, error } = useTravelEsim(id);
  use404Redirect(error);
  const isOctaveAdmin = getUserRole() === 'ADMIN';
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-9 w-56 animate-pulse rounded-xl bg-white" />
        <div className="h-52 animate-pulse rounded-2xl bg-white" />
        <div className="h-48 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }

  if (error || !esim) {
    return (
      <div className="py-12 text-center text-sm text-red-500">
        Impossible de charger cette eSIM internationale.
      </div>
    );
  }

  return (
    <motion.div
      variants={stagger}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={fadeUp} className="flex items-center justify-between gap-3">
        <button
          onClick={() => navigate(`/o/${tenantSlug}/travel-esims`)}
          className="group flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-slate-400 transition-all hover:bg-white hover:text-slate-600 hover:shadow-sm cursor-pointer"
        >
          <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          Retour aux eSIM internationales
        </button>
        {isOctaveAdmin && (
          <Button
            variant="outline"
            size="sm"
            className="text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => setIsDeleteOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Supprimer l'eSIM
          </Button>
        )}
      </motion.div>

      <TravelEsimHeroCard esim={esim} />

      <TravelEsimPlansCard
        esim={esim}
        tenantSlug={tenantSlug}
        today={localDateKey(new Date())}
      />

      {esim.beneficiary.kind === 'LINE' && (
        <motion.div variants={fadeUp}>
          <Link
            to={`/o/${tenantSlug}/lines/${esim.beneficiary.lineId}?tab=travel-esim`}
            className="group inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
          >
            Voir la ligne du collaborateur
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      )}

      <DeleteTravelEsimDialog
        esim={isDeleteOpen ? esim : null}
        onClose={() => setIsDeleteOpen(false)}
        onDeleted={() => navigate(`/o/${tenantSlug}/travel-esims`)}
      />
    </motion.div>
  );
}
